import { exhaustiveSwitchError } from './exhaustiveSwitchError'
import { isNullOrUndefined } from './functions'

export type AffordableConstraintTypeForPartialUnitWithUnitType =
  | 'maxAllowedRent'
  | 'fixedRent'
  | 'none'
export type PartialUnitWithUnitType = {
  // TODO(patricia): Make this required when unit.unitTypeId is required in the db (see
  //  https://airtable.com/appqubGfL6klzmCeI/tblS7Q2acQmEdMN3l/rec8QSrCIEKekP87B?blocks=hide)
  unitType?:
    | {
        isAffordable?: boolean | null
        affordableConstraintType: AffordableConstraintTypeForPartialUnitWithUnitType
        maxAllowedRent?: number | null
        fixedRent?: number | null
        customUnitGroupingId?: number | null
        minimumAllowedBaseRent?: number | null
      }
    | null
    | undefined
}

export type AffordableParams =
  | {
      isAffordable: false
    }
  | {
      isAffordable: true
      affordableConstraintType: AffordableConstraintTypeForPartialUnitWithUnitType
      maxAllowedRent: number | null
      fixedRent: number | null
    }

export function generateAffordableParamsForUnit(unit: PartialUnitWithUnitType): AffordableParams {
  if (isNullOrUndefined(unit.unitType) || !isUnitAffordable(unit)) {
    return {
      isAffordable: false,
    }
  }
  return {
    isAffordable: isUnitAffordable(unit),
    affordableConstraintType: unit.unitType.affordableConstraintType,
    maxAllowedRent: unit.unitType.maxAllowedRent ?? null,
    fixedRent: unit.unitType.fixedRent ?? null,
  }
}

export function applyAffordableConstraintToMonthlyRent(
  affordableParams: AffordableParams,
  rent: number
): number {
  if (!affordableParams.isAffordable) {
    return rent
  }

  let constrainedRent = rent
  switch (affordableParams.affordableConstraintType) {
    case 'maxAllowedRent':
      constrainedRent = affordableParams.maxAllowedRent
        ? Math.min(rent, affordableParams.maxAllowedRent)
        : rent
      break
    case 'fixedRent':
      constrainedRent = affordableParams.fixedRent ?? rent
      break
    case 'none':
      // No constraint - return rent as-is
      constrainedRent = rent
      break
    default:
      throw exhaustiveSwitchError(affordableParams.affordableConstraintType)
  }

  return constrainedRent
}

export function applyAffordableAndMaxRentIncreaseConstraintsToNewLease(
  affordableParams: AffordableParams,
  maxNewLeasePriceBasedOnRentIncreasePercent: number | null | undefined,
  unconstrainedRent: number
): number {
  const affordableConstrainedMarketRent = applyAffordableConstraintToMonthlyRent(
    affordableParams,
    unconstrainedRent
  )

  if (affordableParams.isAffordable) {
    return affordableConstrainedMarketRent
  } else {
    const newConstrainedMarketRent = isNullOrUndefined(maxNewLeasePriceBasedOnRentIncreasePercent)
      ? affordableConstrainedMarketRent
      : Math.min(maxNewLeasePriceBasedOnRentIncreasePercent, affordableConstrainedMarketRent)
    return newConstrainedMarketRent
  }
}

/**
 * Returns true if the unit is considered affordable, false otherwise.
 *
 * @todo #possibly-customer-specific We ignore unit.isAffordable at AIR's request. We should
 *  probably make this a firm-level exception. For Yardi and Resman firms, there doesn't appear to
 *  be a way to populate unit.isAffordable, so we rely solely on unit.unitType.isAffordable.
 */
export function isUnitAffordable<Unit extends PartialUnitWithUnitType>(unit: Unit): boolean {
  return unit.unitType?.isAffordable ?? false
}

/**
 * Returns `true` when the group consists entirely of affordable units (and is non-empty).
 *
 * Used by `PriceRecommendationService`, `InsightsService`, and the recommendation explanation as the
 * shared "all-affordable" determination. For such groups, the price recommendation is computed using
 * the affordable units themselves rather than excluding them from consideration.
 */
export function isAllAffordableUnitGroup<Unit extends PartialUnitWithUnitType>(
  units: Unit[]
): boolean {
  return units.length > 0 && units.every(isUnitAffordable)
}

/**
 * Returns `true` when a unit group is eligible to have a price recommendation generated for it.
 *
 * Mirrors the backend's recommendation-generation criteria in `PriceRecommendationService`: a group
 * is eligible when it has at least one available unit and not every unit is a fixed-rent affordable
 * unit (which has no pricing freedom). All-affordable groups with at least one non-fixed-rent unit
 * are eligible — they get a recommendation based on their affordable units.
 */
export function isUnitGroupEligibleForPriceRecommendation<
  Unit extends PartialUnitWithUnitType & { isAvailable: boolean },
>(units: Unit[]): boolean {
  const hasAvailableUnit = units.some(unit => unit.isAvailable)
  if (!hasAvailableUnit) {
    return false
  }
  return !units.every(isUnitFixedRentAffordable)
}

export function getMaxAffordableRentIfApplicable(
  affordableParams: AffordableParams
): number | null {
  if (!affordableParams.isAffordable) {
    return null
  }
  if (affordableParams.affordableConstraintType === 'maxAllowedRent') {
    return affordableParams.maxAllowedRent ?? null
  }
  return null
}

export function getFixedRentConstraintIfApplicable(
  affordableParams: AffordableParams
): number | null {
  if (!affordableParams.isAffordable) {
    return null
  }
  if (affordableParams.affordableConstraintType === 'fixedRent') {
    return affordableParams.fixedRent ?? null
  }
  return null
}

export function getAffordableConstraintIfApplicable(
  affordableParams: AffordableParams
): number | null {
  if (!affordableParams.isAffordable) {
    return null
  }
  if (affordableParams.affordableConstraintType === 'maxAllowedRent') {
    return affordableParams.maxAllowedRent ?? null
  }
  if (affordableParams.affordableConstraintType === 'fixedRent') {
    return affordableParams.fixedRent ?? null
  }
  if (affordableParams.affordableConstraintType === 'none') {
    return null
  }
  return null
}

/**
 * Returns `true` when an affordable unit has either a max rent limit or a
 * fixed rent constraint configured.
 */
export function hasAffordableConstraint(affordableParams: AffordableParams): boolean {
  if (!affordableParams.isAffordable) {
    return false
  }

  return (
    getMaxAffordableRentIfApplicable(affordableParams) !== null ||
    getFixedRentConstraintIfApplicable(affordableParams) !== null
  )
}

/**
 * Returns `true` when the given affordable constraint type imposes a rent
 * limit (i.e. `maxAllowedRent` or `fixedRent`). Returns `false` for `none`
 * or when the constraint type is not set. Throws for unexpected values.
 */
export function affordableConstraintTypeHasAmount(
  constraintType: AffordableConstraintTypeForPartialUnitWithUnitType | null | undefined
): boolean {
  if (isNullOrUndefined(constraintType)) {
    return false
  }
  switch (constraintType) {
    case 'maxAllowedRent':
    case 'fixedRent':
      return true
    case 'none':
      return false
    default:
      throw exhaustiveSwitchError(constraintType)
  }
}

export type CommonFixedRentAffordableRenewalBehavior = 'respectFixedRent' | 'respectRentControl'
export type UnitWithFixedRentAffordableRenewalBehavior = {
  firm: {
    fixedRentAffordableRenewalBehavior?: CommonFixedRentAffordableRenewalBehavior | null
  }
}

// For now, this is a firm-wide setting. In the future, we may want to add property-level overrides.
// See discussion at https://rentanaworkspace.slack.com/archives/C04H74JBYTW/p1721671791449209
export function getFixedRentAffordableRenewalBehaviorForUnitIfDefined(
  unit: UnitWithFixedRentAffordableRenewalBehavior
): CommonFixedRentAffordableRenewalBehavior | null {
  return unit.firm.fixedRentAffordableRenewalBehavior ?? null
}

export function doesMaxRentApply(
  unit: PartialUnitWithUnitType & UnitWithFixedRentAffordableRenewalBehavior
): boolean {
  const affordableParams = generateAffordableParamsForUnit(unit)
  const fixedRentConstraintIfApplicable = getFixedRentConstraintIfApplicable(affordableParams)
  if (fixedRentConstraintIfApplicable === null) {
    return true
  }

  const fixedRentAffordableRenewalBehavior =
    getFixedRentAffordableRenewalBehaviorForUnitIfDefined(unit)
  if (fixedRentAffordableRenewalBehavior === null) {
    return false
  }
  switch (fixedRentAffordableRenewalBehavior) {
    case 'respectFixedRent':
      return false
    case 'respectRentControl':
      return true
    default:
      throw exhaustiveSwitchError(fixedRentAffordableRenewalBehavior)
  }
}

export function isUnitFixedRentAffordable(unit: PartialUnitWithUnitType): boolean {
  if (!isUnitAffordable(unit)) {
    return false
  }

  const affordableParams = generateAffordableParamsForUnit(unit)
  const fixedRentConstraintIfApplicable = getFixedRentConstraintIfApplicable(affordableParams)
  if (fixedRentConstraintIfApplicable === null) {
    return false
  }
  return true
}

export function doesFixedRentApply(
  unit: PartialUnitWithUnitType & UnitWithFixedRentAffordableRenewalBehavior
): boolean {
  const affordableParams = generateAffordableParamsForUnit(unit)
  const fixedRentConstraintIfApplicable = getFixedRentConstraintIfApplicable(affordableParams)
  if (fixedRentConstraintIfApplicable === null) {
    return false
  }

  const fixedRentAffordableRenewalBehavior =
    getFixedRentAffordableRenewalBehaviorForUnitIfDefined(unit)
  if (fixedRentAffordableRenewalBehavior === null) {
    return false
  }
  switch (fixedRentAffordableRenewalBehavior) {
    case 'respectFixedRent':
      return true
    case 'respectRentControl':
      return false
    default:
      throw exhaustiveSwitchError(fixedRentAffordableRenewalBehavior)
  }
}

export function applyAffordableConstraintToRenewal(
  affordableParams: AffordableParams,
  maxRenewalRent: number,
  fixedRentAffordableRenewalBehavior: CommonFixedRentAffordableRenewalBehavior | null
) {
  const fixedRentConstraintIfApplicable = getFixedRentConstraintIfApplicable(affordableParams)
  if (fixedRentConstraintIfApplicable !== null) {
    if (fixedRentAffordableRenewalBehavior === null) {
      throw new Error(
        'cannot apply fixed-rent constraint to renewal, because fixedRentAffordableRenewalBehavior is not defined'
      )
    }

    switch (fixedRentAffordableRenewalBehavior) {
      case 'respectFixedRent':
        return fixedRentConstraintIfApplicable
      case 'respectRentControl':
        return Math.min(maxRenewalRent, fixedRentConstraintIfApplicable)
      default:
        throw exhaustiveSwitchError(fixedRentAffordableRenewalBehavior)
    }
  }

  return applyAffordableConstraintToMonthlyRent(affordableParams, maxRenewalRent)
}
